import { NavigateFunction } from "react-router-dom";
import { create } from "zustand";
import { AxiosError } from "axios";
import { createApi } from "../../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";

export interface RemoteLinkSite {
  id: number;
  name: string;
  parent: number;
  parent_account: boolean;
  site_count: number;
  group_count: number;
  max_batch_size: number | null;
  minimum_resolution_height: number | null;
  // Set by the backend for single-site groups only; used to label the
  // mapping drop-down with the site number and link to the site page.
  site_id?: number | null;
  site_name?: string | null;
}

export interface MappedSite {
  id: number;
  api_user: number;
  api_user_username: string;
  top_parent: number;
  top_parent_name: string;
  immix_group_id: number;
  immix_group_name: string;
  admin_group: number;
  admin_group_name: string;
  date_created: string;
  date_mapped: string;
  last_attempt_message: string;
}

interface UnmappedSite {
  id: number;
  api_user: number;
  api_user_username: string;
  top_parent: number;
  top_parent_name: string;
  immix_group_id: number;
  immix_group_name: string;
  admin_group: number | null;
  date_created: string;
  date_mapped: string | null;
  last_attempt_message: string;
}

// "Download Sites Overview" runs off-request on the backend and can take
// minutes for a large group. Its progress lives in the store, not in the
// menu component, so leaving the Sites page and coming back still shows the
// notice and keeps the menu item disabled until the build finishes.
export type SitesOverviewNotice = {
  severity: "info" | "success" | "error";
  message: string;
  // Set on the ready notice: a click-to-download fallback in case the
  // browser did not start the download on its own.
  url?: string;
} | null;

/**
 * Background load of a big group's sites (ENG-2634): the first page renders
 * right away, the rest arrive while this is set. `failed` means some pages
 * could not be loaded and the list is incomplete.
 */
export interface GroupSitesProgress {
  groupId: number | string;
  loaded: number;
  total: number;
  done: boolean;
  failed: boolean;
}

interface SitesPageStore {
  sitesOverviewNotice: SitesOverviewNotice;
  setSitesOverviewNotice: (notice: SitesOverviewNotice) => void;

  sitesOverviewInProgress: boolean;
  setSitesOverviewInProgress: (inProgress: boolean) => void;

  selectedName: string;
  setSelectedName: (name: string) => void;

  batchSize: number;
  setBatchSize: (batchSize: number) => void;

  minimumResolutionHeight: number;
  setMinimumResolutionHeight: (minimumResolutionHeight: number) => void;

  updatingBatchSize: boolean;
  setUpdatingBatchSize: (updatingBatchSize: boolean) => void;

  fetchingTenantSettings: boolean;
  setFetchingTenantSettings: (fetchingTenantSettings: boolean) => void;

  selectedGropupId: number;
  setSelectedGropupId: (selectedGropupId: number) => void;

  selectedGroupName: string;
  setSelectedGroupName: (selectedGroupName: string) => void;

  selectedGroupCounts: {
    id?: number;
    name?: string;
    sub_groups?: number;
    sites?: number;
  };

  setSelectedGroupCounts: (selectedGroupCounts: {
    id?: number;
    name?: string;
    sub_groups?: number;
    sites?: number;
  }) => void;

  fetchingSelectedGroupCounts: boolean;
  setFetchingSelectedGroupCounts: (
    fetchingSelectedGroupCounts: boolean
  ) => void;

  // Cache for storing group counts by ID
  groupCountsCache: Record<
    number,
    {
      id?: number;
      name?: string;
      sub_groups?: number;
      sites?: number;
    }
  >;

  immixRemotelinkDialogOpen: boolean;
  setImmixRemotelinkDialogOpen: (immixRemotelinkDialogOpen: boolean) => void;

  mappedSites: MappedSite[];
  setMappedSites: (mappedSites: MappedSite[]) => void;

  unmappedSites: UnmappedSite[];
  setUnmappedSites: (unmappedSites: UnmappedSite[]) => void;

  // Add to cache
  addToGroupCountsCache: (
    groupId: number,
    groupCounts: {
      id?: number;
      name?: string;
      sub_groups?: number;
      sites?: number;
    }
  ) => void;

  // Clear the cache
  clearGroupCountsCache: () => void;

  getSelectedGroupCounts: (
    navigate: NavigateFunction,
    groupId: number
  ) => Promise<void>;

  getTenantSettings: (
    groupId: number,
    navigate: NavigateFunction
  ) => Promise<void>;

  handlePatchTenantSettings: (
    batchSize: number,
    minimumResolutionHeight: number,
    navigate: NavigateFunction
  ) => Promise<void>;

  searchGroupId: number;

  setSearchGroupId: (groupId: number) => void;

  groupSitesProgress: GroupSitesProgress | null;
  setGroupSitesProgress: (progress: GroupSitesProgress | null) => void;

  remoteLinkSites: RemoteLinkSite[];
  setRemotelinkSites: (remoteLinkSites: RemoteLinkSite[]) => void;

  optionsIdsMap: Map<string, number>;
  setOptionsIdsMap: (optionsIdsMap: Map<string, number>) => void;

  fetchingImmixRemoteLinkData: boolean;
  setFetchingImmixRemoteLinkData: (
    fetchingImmixRemoteLinkData: boolean
  ) => void;

  updatingImmixRemoteLinkData: boolean;
  setUpdatingImmixRemoteLinkData: (
    updatingImmixRemoteLinkData: boolean
  ) => void;

  getMappedSites: (navigate: NavigateFunction) => Promise<void>;

  getUnmappedSites: (navigate: NavigateFunction) => Promise<void>;

  getRemotelinkSites: (navigate: NavigateFunction) => Promise<void>;

  getImmixRemoteLinkData: (navigate: NavigateFunction) => Promise<void>;

  updateImmixRemoteLinkData: (
    patchObject: any,
    navigate: NavigateFunction
  ) => Promise<void>;

  // Clears the Actuate group on a Remote Link record so the Immix site goes
  // back to "unmapped" and its signals stop arming/disarming the site. The
  // record is kept (not deleted) on purpose: the next Immix signal would
  // otherwise re-create it and auto-match it by name again.
  unlinkImmixRemoteLinkData: (
    site: MappedSite,
    navigate: NavigateFunction
  ) => Promise<boolean>;

  resetImmixRemoteLinkData: () => void;
}

export const useSitesPageStore = create<SitesPageStore>((set, get) => ({
  sitesOverviewNotice: null,
  setSitesOverviewNotice: (sitesOverviewNotice) => set({ sitesOverviewNotice }),

  sitesOverviewInProgress: false,
  setSitesOverviewInProgress: (sitesOverviewInProgress) =>
    set({ sitesOverviewInProgress }),

  selectedName: "",
  setSelectedName: (name) => set({ selectedName: name }),

  batchSize: 0,
  setBatchSize: (batchSize) => set({ batchSize }),

  minimumResolutionHeight: 0,
  setMinimumResolutionHeight: (minimumResolutionHeight) =>
    set({ minimumResolutionHeight }),

  updatingBatchSize: false,
  setUpdatingBatchSize: (updatingBatchSize) => set({ updatingBatchSize }),

  fetchingTenantSettings: false,
  setFetchingTenantSettings: (fetchingTenantSettings) =>
    set({ fetchingTenantSettings }),

  selectedGropupId: 0,
  setSelectedGropupId: (selectedGropupId) => set({ selectedGropupId }),

  selectedGroupName: "",
  setSelectedGroupName: (selectedGroupName) => set({ selectedGroupName }),

  selectedGroupCounts: {},

  setSelectedGroupCounts: (selectedGroupCounts) => set({ selectedGroupCounts }),

  fetchingSelectedGroupCounts: false,

  setFetchingSelectedGroupCounts: (fetchingSelectedGroupCounts) =>
    set({ fetchingSelectedGroupCounts }),

  // Initialize the cache as an empty object
  groupCountsCache: {},

  immixRemotelinkDialogOpen: false,
  setImmixRemotelinkDialogOpen: (immixRemotelinkDialogOpen: boolean) =>
    set({ immixRemotelinkDialogOpen }),

  mappedSites: [],
  setMappedSites: (mappedSites) => set({ mappedSites }),

  unmappedSites: [],
  setUnmappedSites: (unmappedSites) => set({ unmappedSites }),

  // Method to add data to the cache
  addToGroupCountsCache: (groupId, groupCounts) =>
    set((state) => ({
      groupCountsCache: {
        ...state.groupCountsCache,
        [groupId]: groupCounts,
      },
    })),

  // Method to clear the cache
  clearGroupCountsCache: () => set({ groupCountsCache: {} }),

  // Initialize healthCheckMetricsData as an empty array

  getSelectedGroupCounts: async (navigate, groupId) => {
    const {
      setSelectedGroupCounts,
      setFetchingSelectedGroupCounts,
      groupCountsCache,
      addToGroupCountsCache,
    } = get();

    // Check if we already have this data in the cache
    if (groupCountsCache[groupId]) {
      // Use cached data instead of making an API call
      setSelectedGroupCounts(groupCountsCache[groupId]);
      // If a fetch for another item was active, and we hit cache for this one,
      // ensure fetching flag is false if this cached item is now the target.
      // This specific line might need adjustment based on overall app flow,
      // but for now, if we serve from cache, fetching should ideally be false for this item.
      if (get().selectedGroupCounts?.id === groupId) {
        setFetchingSelectedGroupCounts(false);
      }
      return;
    } // Not in cache, proceed with API call:
    // 1. Set fetching flag to true.
    setFetchingSelectedGroupCounts(true);
    // 2. Set selectedGroupCounts to an intermediate state indicating which group is loading.
    // This helps UI to know *which* item is loading if multiple onOpen events fire rapidly.
    // The absence of count fields (sites, sub_groups) signifies loading.
    setSelectedGroupCounts({ id: groupId });

    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getSelectedGroupCounts(navigate, groupId);
    };

    try {
      const response = await AxiosUI.get(`group/${groupId}/counts/`);
      const selectedGroupCountsData = response.data;

      // Store in cache for future use
      addToGroupCountsCache(groupId, selectedGroupCountsData);

      // Update current selection, but only if this groupId is still the active target
      // and we are still in a fetching state for it.
      if (
        get().selectedGroupCounts?.id === groupId &&
        get().fetchingSelectedGroupCounts
      ) {
        setSelectedGroupCounts(selectedGroupCountsData);
      }
    } catch (reason) {
      // Handle error, but only if this groupId is still the active target.
      if (
        get().selectedGroupCounts?.id === groupId &&
        get().fetchingSelectedGroupCounts
      ) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            // Clear counts for this specific group on error, if it's still the target.
            if (get().selectedGroupCounts?.id === groupId) {
              setSelectedGroupCounts({ id: groupId }); // Or simply {} if preferred for error state
            }
          }
        );
      }
    } finally {
      // Set fetching to false, but only if this groupId's fetch is completing
      // and it's still the active target.
      if (
        get().selectedGroupCounts?.id === groupId &&
        get().fetchingSelectedGroupCounts
      ) {
        setFetchingSelectedGroupCounts(false);
      }
    }
  },

  getTenantSettings: async (groupId, navigate) => {
    const {
      setBatchSize,
      setFetchingTenantSettings,
      setMinimumResolutionHeight,
    } = get();
    setFetchingTenantSettings(true);
    setBatchSize(0);
    setMinimumResolutionHeight(0);
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getTenantSettings(groupId, navigate);
    };
    try {
      const response = await AxiosUI.get(`group/${groupId}/`);
      setBatchSize(response.data?.max_batch_size || 0);
      setMinimumResolutionHeight(response.data?.minimum_resolution_height || 0);
      setFetchingTenantSettings(false);
    } catch (reason) {
      setFetchingTenantSettings(false);
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setBatchSize(0);
          setMinimumResolutionHeight(0);
        }
      );
    }
  },

  handlePatchTenantSettings: async (
    batchSize,
    minimumResolutionHeight,
    navigate
  ) => {
    const {
      setUpdatingBatchSize,
      selectedGropupId,
      setBatchSize,
      setMinimumResolutionHeight,
    } = get();
    setUpdatingBatchSize(true);
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().handlePatchTenantSettings(
        batchSize,
        minimumResolutionHeight,
        navigate
      );
    };
    try {
      await AxiosUI.patch(`group/${selectedGropupId}/`, {
        max_batch_size: batchSize,
        minimum_resolution_height: minimumResolutionHeight,
      });
      setBatchSize(batchSize);
      setMinimumResolutionHeight(minimumResolutionHeight);
      setUpdatingBatchSize(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setUpdatingBatchSize(false);
        }
      );
    }
  },

  searchGroupId: 0,

  setSearchGroupId: (groupId) => set({ searchGroupId: groupId }),

  groupSitesProgress: null,
  setGroupSitesProgress: (groupSitesProgress) => set({ groupSitesProgress }),

  remoteLinkSites: [],
  setRemotelinkSites: (remoteLinkSites) => set({ remoteLinkSites }),

  optionsIdsMap: new Map<string, number>(),
  setOptionsIdsMap: (optionsIdsMap) => set({ optionsIdsMap }),

  fetchingImmixRemoteLinkData: false,
  setFetchingImmixRemoteLinkData: (fetchingImmixRemoteLinkData) =>
    set({ fetchingImmixRemoteLinkData }),

  updatingImmixRemoteLinkData: false,
  setUpdatingImmixRemoteLinkData: (updatingImmixRemoteLinkData) =>
    set({ updatingImmixRemoteLinkData }),

  getMappedSites: async (navigate: NavigateFunction) => {
    const { getMappedSites, setMappedSites } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getMappedSites(navigate);
    };
    try {
      const response = await AxiosUI.get(`remote_link/?is_mapped=true`);
      setMappedSites(response.data.results);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  getUnmappedSites: async (navigate: NavigateFunction) => {
    const { getUnmappedSites, setUnmappedSites } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getUnmappedSites(navigate);
    };
    try {
      const response = await AxiosUI.get(`remote_link/?is_mapped=false`);
      setUnmappedSites(response.data.results);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  getRemotelinkSites: async (navigate) => {
    const { setRemotelinkSites, setOptionsIdsMap } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getRemotelinkSites(navigate);
    };
    try {
      const response = await AxiosUI.get(
        `group/?group_type=remote_link&page_size=20000`
      );
      setRemotelinkSites(response.data.results);
      const optionsIds = new Map<string, number>();
      response.data.results.forEach((rlSite: RemoteLinkSite) => {
        optionsIds.set(rlSite.name, rlSite.id);
      });
      setOptionsIdsMap(optionsIds);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  getImmixRemoteLinkData: async (navigate: NavigateFunction) => {
    const {
      setFetchingImmixRemoteLinkData,
      getMappedSites,
      getUnmappedSites,
      getRemotelinkSites,
    } = get();
    setFetchingImmixRemoteLinkData(true);
    try {
      await Promise.all([
        getMappedSites(navigate),
        getUnmappedSites(navigate),
        getRemotelinkSites(navigate),
      ]);
    } finally {
      setFetchingImmixRemoteLinkData(false);
    }
  },

  updateImmixRemoteLinkData: async (
    patchObject: any,
    navigate: NavigateFunction
  ) => {
    const {
      setUpdatingImmixRemoteLinkData,
      setMappedSites,
      mappedSites,
      setUnmappedSites,
      unmappedSites,
    } = get();
    setUpdatingImmixRemoteLinkData(true);
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().updateImmixRemoteLinkData(patchObject, navigate);
    };
    try {
      const response = await AxiosUI.patch(`remote_link/${patchObject.id}/`, {
        admin_group: patchObject.admin_group_id,
      });
      setMappedSites([...mappedSites, response.data]);
      setUnmappedSites(
        unmappedSites.filter((site) => site.id !== patchObject.id)
      );
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Error updating Immix Remotelink data, please try again");
        }
      );
    } finally {
      setUpdatingImmixRemoteLinkData(false);
    }
  },

  unlinkImmixRemoteLinkData: async (
    site: MappedSite,
    navigate: NavigateFunction
  ) => {
    const { setUpdatingImmixRemoteLinkData } = get();
    setUpdatingImmixRemoteLinkData(true);
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().unlinkImmixRemoteLinkData(site, navigate);
    };
    try {
      const response = await AxiosUI.patch(`remote_link/${site.id}/`, {
        admin_group: null,
      });
      // Re-read the lists at completion time: another save may have landed
      // while the request was in flight.
      const { mappedSites, unmappedSites } = get();
      get().setMappedSites(mappedSites.filter((s) => s.id !== site.id));
      get().setUnmappedSites([
        ...unmappedSites.filter((s) => s.id !== site.id),
        { ...response.data, admin_group: null },
      ]);
      return true;
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Error unlinking Immix Remotelink site, please try again");
        }
      );
      return false;
    } finally {
      setUpdatingImmixRemoteLinkData(false);
    }
  },

  resetImmixRemoteLinkData: () => {
    const {
      setMappedSites,
      setUnmappedSites,
      setOptionsIdsMap,
      setUpdatingImmixRemoteLinkData,
    } = get();
    setMappedSites([]);
    setUnmappedSites([]);
    setOptionsIdsMap(new Map<string, number>());
    setUpdatingImmixRemoteLinkData(false);
  },
}));
